import './index.css';
import './components/ui/selection/omni-selection-override.css';
import { Amplify } from 'aws-amplify';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createRoot } from 'react-dom/client';

console.log('index.tsx: Starting application...');
console.log(
  'index.tsx: Raw process.env check:',
  typeof process !== 'undefined' ? 'process exists' : 'process undefined',
);
console.log(
  'index.tsx: All REACT_APP_ vars:',
  Object.keys(process.env || {}).filter((key) => key.startsWith('REACT_APP_')),
);

// Test individual environment variables
const userPoolId = process.env.REACT_APP_USER_POOL_ID;
const userPoolClientId = process.env.REACT_APP_USER_WEB_CLIENT_ID;
const region = process.env.REACT_APP_AWS_REGION;

console.log('index.tsx: Individual env vars test:', {
  userPoolId: userPoolId,
  userPoolClientId: userPoolClientId,
  region: region,
  userPoolIdType: typeof userPoolId,
  userPoolClientIdType: typeof userPoolClientId,
});

// Only proceed if we have the required variables
if (!userPoolId || !userPoolClientId) {
  console.error(
    'index.tsx: CRITICAL - Required environment variables missing!',
  );
  console.error('index.tsx: userPoolId:', userPoolId);
  console.error('index.tsx: userPoolClientId:', userPoolClientId);
  console.error('index.tsx: Check your .env file and Vite configuration');
} else {
  console.log(
    'index.tsx: Environment variables look good, proceeding with Amplify config...',
  );
}

// Log OAuth configuration status
const oauthDomain = process.env.REACT_APP_OAUTH_DOMAIN;
if (oauthDomain) {
  console.log('index.tsx: OAuth is ENABLED with domain:', oauthDomain);
} else {
  console.log('index.tsx: OAuth is DISABLED - no domain configured');
}

// Configure Amplify immediately in index.tsx
const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId: userPoolId || '',
      userPoolClientId: userPoolClientId || '',
      identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID || '',
      signUpVerificationMethod: 'code' as const,
      // Include OAuth configuration with the correct domain
      loginWith: {
        oauth: {
          domain: process.env.REACT_APP_OAUTH_DOMAIN || '',
          scopes: ['openid', 'aws.cognito.signin.user.admin', 'profile'],
          redirectSignIn: [
            process.env.REACT_APP_REDIRECT_SIGN_IN ||
            'http://localhost:3000/callback',
          ],
          redirectSignOut: [
            process.env.REACT_APP_REDIRECT_SIGN_OUT || 'http://localhost:3000/',
          ],
          responseType: 'token' as const,
        },
      },
    },
  },
  API: {
    GraphQL: {
      endpoint: process.env.REACT_APP_GRAPHQL_ENDPOINT || '',
      region: region || 'eu-west-1',
      defaultAuthMode: 'userPool' as const,
    },
  },
  Storage: {
    S3: {
      bucket: process.env.REACT_APP_USER_FILES_S3_BUCKET || '',
      region: region || 'eu-west-1',
    },
  },
};

console.log(
  'index.tsx: Final amplifyConfig:',
  JSON.stringify(amplifyConfig, null, 2),
);

if (userPoolId && userPoolClientId) {
  try {
    console.log('index.tsx: Configuring Amplify...');
    Amplify.configure(amplifyConfig);
    console.log('index.tsx: ✅ Amplify configured successfully!');
  } catch (error) {
    console.error('index.tsx: ❌ Failed to configure Amplify:', error);
  }
} else {
  console.error(
    'index.tsx: ❌ Skipping Amplify configuration due to missing environment variables',
  );
}

import App from './App';
import * as serviceWorker from './serviceWorker';
import { Provider } from './components/ui/provider';
import { GlobalProvider } from './context/provider';
import { QueryBuilderProvider } from './context/queryBuilderProvider';
import { AuthProvider } from './context/authProvider';
import { CoachmarkProvider } from './components/ui/coachmarks';

const ThemedApp = () => (
  <Provider>
    <DndProvider backend={HTML5Backend}>
      <GlobalProvider>
        <QueryBuilderProvider>
          <AuthProvider>
            <CoachmarkProvider onAnalyticsEvent={(eventType, data) => {
              console.log('Coachmark Analytics:', eventType, data);
              // Here you could integrate with your analytics service
              // e.g., analytics.track(eventType, data);
            }}>
              <App />
            </CoachmarkProvider>
          </AuthProvider>
        </QueryBuilderProvider>
      </GlobalProvider>
    </DndProvider>
  </Provider>
);

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(<ThemedApp />);
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
